import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Col, Container, Form, Row, Spinner } from "react-bootstrap";
import DynamicBreadcrumb from "../header/breadcrumb";
import { showConfirm } from "react-confirm-prompt";
import branchApi from "../../services/branchapi";
import branchZonesApi from "../../services/branchZonesApi";
import { loadGoogleMaps } from "../../utils/googleMapsLoader";
import { distanceMeters, findOverlap, getMergeCandidates, getMergeSuggestion, getOverlapDetails, getOverlapScore, MERGE_DISTANCE_METERS } from "../../utils/zoneGeometry";

// ---------- GeoJSON helpers ----------
const ensureClosedRing = (ring) => {
  if (!ring || ring.length < 3) return ring;
  const first = ring[0];
  const last = ring[ring.length - 1];
  if (first[0] !== last[0] || first[1] !== last[1]) {
    return [...ring, [first[0], first[1]]];
  }
  return ring;
};

const polygonToGeoJSON = (poly) => {
  const path = poly.getPath().getArray();
  const ring = path.map((p) => [Number(p.lng()), Number(p.lat())]); // [lng,lat]
  const closed = ensureClosedRing(ring);
  if (!closed || closed.length < 4) throw new Error("Polygon needs at least 3 points");
  return { type: "Polygon", coordinates: [closed] };
};

const geoJSONToPath = (geo) => {
  const ring = geo?.coordinates?.[0] || [];
  const path = ring.map(([lng, lat]) => ({ lat: Number(lat), lng: Number(lng) }));
  if (path.length > 1 && path[0].lat === path[path.length - 1].lat && path[0].lng === path[path.length - 1].lng) path.pop();
  return path;
};

// ---- stable colors for other branches ----
const COLORS = [
  "#3b82f6",
  "#ef4444",
  "#22c55e",
  "#a855f7",
  "#f59e0b",
  "#06b6d4",
  "#84cc16",
  "#ec4899",
  "#14b8a6",
  "#f97316",
  "#8b5cf6",
  "#10b981",
];

const colorForBranch = (branchId) => {
  const n = Number(branchId) || 0;
  return COLORS[Math.abs(n) % COLORS.length];
};

export default function BranchZones() {
  const { branchId } = useParams();
  const navigate = useNavigate();

  const mapDivRef = useRef(null);
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const drawingRef = useRef(null);

  const drawnPolygonRef = useRef(null);
  const existingPolygonRef = useRef(null);
  const branchMarkerRef = useRef(null);

  const otherPolygonsRef = useRef([]);
  const otherZonesDataRef = useRef([]);
  const otherInfoWindowsRef = useRef([]);
  const editPathListenersRef = useRef([]);
  const preMergePathRef = useRef(null);
  const overlapMarkersRef = useRef([]);
  const editStartPathRef = useRef(null);
  const revertingPathRef = useRef(false);
  const acceptedOverlapScoreRef = useRef(0);

  // ---- RIGHT-CLICK distance helpers ----
  const branchLatLngRef = useRef(null);

  const rightClickMapListenerRef = useRef(null);
  const rightClickPolyListenerRef = useRef(null);
  const contextMenuBlockRef = useRef(null);

  const isEditableRef = useRef(false);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [branch, setBranch] = useState(null);

  const [zoneId, setZoneId] = useState(null);
  const [zoneName, setZoneName] = useState("");

  const [isEditable, setIsEditable] = useState(false);
  const [isMapFullscreen, setIsMapFullscreen] = useState(false);
  const [mergeSuggestion, setMergeSuggestion] = useState(null);
  const [overlapZone, setOverlapZone] = useState(null);
  const [overlapDetails, setOverlapDetails] = useState([]);
  const [editNotice, setEditNotice] = useState("");
  const [selectedVertexIndex, setSelectedVertexIndex] = useState(null);

  // shown only after right-click
  const [distanceKm, setDistanceKm] = useState(null);

  const zoneType = "PRIMARY";
  const priority = 1;

  const clearEditPathListeners = useCallback(() => {
    editPathListenersRef.current.forEach((listener) => window.google?.maps?.event?.removeListener(listener));
    editPathListenersRef.current = [];
  }, []);

  const evaluateMergeSuggestion = useCallback((poly) => {
    if (!poly) return setMergeSuggestion(null);
    const path = poly.getPath().getArray().map((p) => ({ lat: p.lat(), lng: p.lng() }));
    setMergeSuggestion(getMergeSuggestion(path, otherZonesDataRef.current, MERGE_DISTANCE_METERS));
    const overlap = findOverlap(path, otherZonesDataRef.current);
    setOverlapZone(overlap);
    const details = getOverlapDetails(path, otherZonesDataRef.current);
    setOverlapDetails(details);
    overlapMarkersRef.current.forEach((marker) => marker.setMap(null));
    overlapMarkersRef.current = [];
    details.forEach((detail) => detail.points.forEach((point) => {
      if (!mapRef.current || !window.google) return;
      overlapMarkersRef.current.push(new window.google.maps.Circle({
        map: mapRef.current,
        center: point,
        radius: 18,
        strokeColor: "#b91c1c",
        strokeOpacity: 1,
        strokeWeight: 3,
        fillColor: "#ef4444",
        fillOpacity: 0.55,
        clickable: false,
        zIndex: 2000,
      }));
    }));
    poly.setOptions({ strokeColor: overlap ? "#dc3545" : undefined, fillColor: overlap ? "#dc3545" : undefined });
  }, []);

  const watchEditablePath = useCallback((poly) => {
    clearEditPathListeners();
    if (!poly) return;
    const path = poly.getPath();
    const vertexFromEvent = (event) => {
      if (Number.isInteger(event?.vertex)) return event.vertex;
      if (!event?.latLng) return null;
      const clicked = { lat: event.latLng.lat(), lng: event.latLng.lng() };
      let best = { index: null, distance: Infinity };
      path.getArray().forEach((point, index) => {
        const distance = distanceMeters(clicked, { lat: point.lat(), lng: point.lng() });
        if (distance < best.distance) best = { index, distance };
      });
      return best.distance <= 25 ? best.index : null;
    };
    const validateOrRevert = (undo) => {
      if (revertingPathRef.current) return;
      const current = path.getArray().map((p) => ({ lat: p.lat(), lng: p.lng() }));
      const nextScore = getOverlapScore(current, otherZonesDataRef.current);
      if (nextScore > acceptedOverlapScoreRef.current) {
        const overlap = findOverlap(current, otherZonesDataRef.current);
        revertingPathRef.current = true;
        undo();
        revertingPathRef.current = false;
        setEditNotice(`Cannot increase overlap${overlap ? ` with ${overlap.branchName || `Branch #${overlap.branchId}`}` : ""}. The change was reversed.`);
      } else {
        acceptedOverlapScoreRef.current = nextScore;
        setEditNotice(nextScore ? "Correction accepted. Continue editing to remove the remaining overlap." : "Valid border—no overlap remains.");
      }
      evaluateMergeSuggestion(poly);
    };
    editPathListenersRef.current = [
      path.addListener("set_at", (index, previous) => validateOrRevert(() => path.setAt(index, previous))),
      path.addListener("insert_at", (index) => validateOrRevert(() => path.removeAt(index))),
      path.addListener("remove_at", (index, removed) => validateOrRevert(() => path.insertAt(index, removed))),
      poly.addListener("click", (event) => setSelectedVertexIndex(vertexFromEvent(event))),
      poly.addListener("rightclick", (event) => {
        const vertex = vertexFromEvent(event);
        if (!Number.isInteger(vertex)) return;
        if (path.getLength() <= 3) {
          setEditNotice("A delivery zone must keep at least 3 points.");
          return;
        }
        path.removeAt(vertex);
        setSelectedVertexIndex(null);
      }),
    ];
    evaluateMergeSuggestion(poly);
  }, [clearEditPathListeners, evaluateMergeSuggestion]);

  const pathArray = useMemo(
    () => [
      { name: "Branchs List", url: "/admin/branches" },
      { name: "Delivery Zones", url: null },
    ],
    []
  );

  const clearPolygons = useCallback(() => {
    if (drawnPolygonRef.current) {
      drawnPolygonRef.current.setMap(null);
      drawnPolygonRef.current = null;
    }
    if (existingPolygonRef.current) {
      existingPolygonRef.current.setMap(null);
      existingPolygonRef.current = null;
    }
  }, []);

  const clearOtherPolygons = useCallback(() => {
    if (otherPolygonsRef.current?.length) {
      otherPolygonsRef.current.forEach((p) => p.setMap(null));
    }
    otherPolygonsRef.current = [];

    if (otherInfoWindowsRef.current?.length) {
      otherInfoWindowsRef.current.forEach((iw) => iw.close());
    }
    otherInfoWindowsRef.current = [];
  }, []);

  const setCurrentPolygon = useCallback(
    (poly, { asExisting = false } = {}) => {
      clearPolygons();
      if (asExisting) existingPolygonRef.current = poly;
      else drawnPolygonRef.current = poly;
    },
    [clearPolygons]
  );

  const cleanupRightClick = useCallback(() => {
    if (rightClickMapListenerRef.current) {
      window.google?.maps?.event?.removeListener(rightClickMapListenerRef.current);
      rightClickMapListenerRef.current = null;
    }
    if (rightClickPolyListenerRef.current) {
      window.google?.maps?.event?.removeListener(rightClickPolyListenerRef.current);
      rightClickPolyListenerRef.current = null;
    }
    if (contextMenuBlockRef.current) {
      contextMenuBlockRef.current();
      contextMenuBlockRef.current = null;
    }
  }, []);

  const measureDistance = useCallback((latLng) => {
    if (!isEditableRef.current) return;
    if (!branchLatLngRef.current) return;
    if (!latLng) return;

    try {
      const meters = window.google.maps.geometry.spherical.computeDistanceBetween(
        branchLatLngRef.current,
        latLng
      );
      const km = meters / 1000;
      setDistanceKm(Number.isFinite(km) ? km : null);
    } catch (e) {
      setDistanceKm(null);
    }
  }, []);

  // ✅ Attach right-click distance to BOTH map and active polygon
  const attachRightClickDistance = useCallback(async () => {
    await loadGoogleMaps(["drawing", "geometry"]);
    if (!mapRef.current) return;

    // cleanup previous to avoid duplicates
    cleanupRightClick();

    // 1) prevent browser context-menu on map div (important!)
    if (mapDivRef.current) {
      const handler = (ev) => ev.preventDefault();
      mapDivRef.current.addEventListener("contextmenu", handler);
      contextMenuBlockRef.current = () => mapDivRef.current?.removeEventListener("contextmenu", handler);
    }

    // 2) listen on MAP
    rightClickMapListenerRef.current = mapRef.current.addListener("rightclick", (e) => {
      if (!isEditableRef.current) return;
      measureDistance(e?.latLng);
    });

    // 3) listen on ACTIVE POLYGON also (because right click on polygon may not bubble to map)
    const poly = existingPolygonRef.current || drawnPolygonRef.current;
    if (poly) {
      rightClickPolyListenerRef.current = poly.addListener("rightclick", (e) => {
        if (!isEditableRef.current) return;
        measureDistance(e?.latLng);
      });
    }
  }, [cleanupRightClick, measureDistance]);

  const initMap = useCallback(
    async (center) => {
      const google = await loadGoogleMaps(["drawing", "geometry"]);
      if (!mapDivRef.current) return;

      if (!mapRef.current) {
        mapRef.current = new google.maps.Map(mapDivRef.current, {
          center,
          zoom: 13,
          mapTypeControl: true,
          streetViewControl: false,
          fullscreenControl: false,
        });

        drawingRef.current = new google.maps.drawing.DrawingManager({
          drawingMode: null,
          drawingControl: false,
          polygonOptions: {
            clickable: true,
            editable: false,
            draggable: false,
            fillOpacity: 0.25,
            strokeWeight: 2,
            zIndex: 999,
          },
        });

        drawingRef.current.setMap(mapRef.current);

        google.maps.event.addListener(drawingRef.current, "polygoncomplete", async (poly) => {
          drawingRef.current.setDrawingMode(null);

          poly.setEditable(true);
          poly.setOptions({ zIndex: 999 });
          setCurrentPolygon(poly, { asExisting: false });
          editStartPathRef.current = poly.getPath().getArray().map((p) => ({ lat: p.lat(), lng: p.lng() }));
          acceptedOverlapScoreRef.current = getOverlapScore(editStartPathRef.current, otherZonesDataRef.current);

          // enable edit mode
          isEditableRef.current = true;
          setIsEditable(true);

          // measure only after right click
          setDistanceKm(null);
          watchEditablePath(poly);

          // reattach so polygon rightclick is also bound to the new poly
          await attachRightClickDistance();
        });

        await attachRightClickDistance();
      } else {
        mapRef.current.setCenter(center);
      }
    },
    [attachRightClickDistance, setCurrentPolygon, watchEditablePath]
  );

  const renderExistingZone = useCallback(
    async (geojson) => {
      await loadGoogleMaps(["drawing", "geometry"]);
      if (!mapRef.current) return;

      const path = geoJSONToPath(geojson);
      if (!path || path.length < 3) return;

      const poly = new window.google.maps.Polygon({
        paths: path,
        clickable: true,
        editable: false,
        draggable: false,
        fillOpacity: 0.25,
        strokeWeight: 2,
        zIndex: 999,
      });

      poly.setMap(mapRef.current);
      setCurrentPolygon(poly, { asExisting: true });

      // not editable by default
      isEditableRef.current = false;
      setIsEditable(false);
      setDistanceKm(null);

      // attach rightclick on this polygon too (once loaded)
      await attachRightClickDistance();

      const bounds = new window.google.maps.LatLngBounds();
      path.forEach((p) => bounds.extend(p));
      mapRef.current.fitBounds(bounds);
    },
    [attachRightClickDistance, setCurrentPolygon]
  );

  const renderOtherZones = useCallback(async () => {
    try {
      if (!mapRef.current) return;
      await loadGoogleMaps(["drawing", "geometry"]);

      clearOtherPolygons();

      const resp = await branchZonesApi.getZonesForMap({
        branchId,
        zoneType: "PRIMARY",
        activeOnly: true,
        includeSelf: false,
      });

      const otherZones = resp?.data?.otherBranches ?? [];

      otherZonesDataRef.current = otherZones.map((z) => {
        let geo = z.geojson;
        if (typeof geo === "string") { try { geo = JSON.parse(geo); } catch (e) { geo = null; } }
        return { ...z, path: geoJSONToPath(geo) };
      }).filter((z) => z.path.length > 2);

      otherZones.forEach((z) => {
        let geo = z.geojson;
        if (typeof geo === "string") {
          try {
            geo = JSON.parse(geo);
          } catch (e) {
            geo = null;
          }
        }

        const ring = geo?.coordinates?.[0] || [];
        if (!ring.length) return;

        const paths = ring.map(([lng, lat]) => ({ lat: Number(lat), lng: Number(lng) }));
        const col = colorForBranch(z.branchId);

        const poly = new window.google.maps.Polygon({
          paths,
          map: mapRef.current,
          clickable: true,
          editable: false,
          draggable: false,
          strokeWeight: 2,
          strokeOpacity: 0.9,
          fillOpacity: 0.18,
          strokeColor: col,
          fillColor: col,
          zIndex: 1,
        });

        const info = new window.google.maps.InfoWindow();
        otherInfoWindowsRef.current.push(info);

        poly.addListener("click", (e) => {
          otherInfoWindowsRef.current.forEach((iw) => iw.close());

          const name = z.branchName || `Branch #${z.branchId}`;
          info.setContent(`
            <div style="font-size:12px; line-height: 1.4;">
              <b>${name}</b><br/>
              Zone: ${z.zoneType || "-"}<br/>
              Priority: ${z.priority ?? "-"}
            </div>
          `);
          info.setPosition(e.latLng);
          info.open({ map: mapRef.current });
        });

        otherPolygonsRef.current.push(poly);
      });
    } catch (e) {
      console.error("renderOtherZones failed:", e);
    }
  }, [branchId, clearOtherPolygons]);

  const buildBranchAddress = (b) => {
    if (!b) return "";
    const parts = [
      b.branchName || b.name,
      b.address || b.branchAddress,
      b.areaName,
      b.city,
      b.state,
      b.pincode,
      b.zipcode,
      b.country,
    ].filter(Boolean);
    return parts.join(", ");
  };

  const geocodeAddress = async (address) => {
    await loadGoogleMaps(["drawing", "geometry"]);
    if (!address) return null;

    return new Promise((resolve) => {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ address }, (results, status) => {
        if (status === "OK" && results?.[0]?.geometry) {
          const loc = results[0].geometry.location;
          resolve({ lat: loc.lat(), lng: loc.lng() });
        } else {
          resolve(null);
        }
      });
    });
  };

  const setBranchMarker = async (center, title) => {
    await loadGoogleMaps(["drawing", "geometry"]);
    if (!mapRef.current || !center) return;

    if (branchMarkerRef.current) {
      branchMarkerRef.current.setMap(null);
      branchMarkerRef.current = null;
    }

    branchMarkerRef.current = new window.google.maps.Marker({
      position: center,
      map: mapRef.current,
      title: title || "Branch",
    });

    branchLatLngRef.current = new window.google.maps.LatLng(center.lat, center.lng);
  };

  const fetchBranchAndZone = useCallback(async () => {
    setLoading(true);
    try {
      const br = await branchApi.getBranchById({ id: branchId });
      const brRow = br?.data?.result?.[0] || null;
      if (!brRow) throw new Error("Branch not found");

      setBranch(brRow);

      const lat = Number(brRow?.latitude);
      const lng = Number(brRow?.longitude);

      let center = null;
      if (!Number.isNaN(lat) && !Number.isNaN(lng) && lat !== 0 && lng !== 0) {
        center = { lat, lng };
      } else {
        const addr = buildBranchAddress(brRow);
        const geo = await geocodeAddress(addr);
        if (geo) center = geo;
      }

      if (!center) center = { lat: 10.0, lng: 78.0 };

      await initMap(center);

      mapRef.current.setZoom(14);
      await setBranchMarker(center, brRow?.branchName || brRow?.name || "Branch");

      await renderOtherZones();

      const zonesResp = await branchZonesApi.getZonesByBranch({
        branchId,
        limit: 10,
        page: 0,
        zoneType,
      });

      const zones = zonesResp?.data?.result || [];
      const primary = zones.length ? zones[0] : null;

      if (primary) {
        setZoneId(primary.id);
        setZoneName(primary.zoneName || "");
        await renderExistingZone(primary.geojson);
      } else {
        setZoneId(null);
        setZoneName("");
        clearPolygons();

        isEditableRef.current = false;
        setIsEditable(false);
        setDistanceKm(null);

        await attachRightClickDistance();
      }
    } catch (e) {
      console.error(e);
      showConfirm("Oops!", {
        description: e.message || "Failed to load branch / zone data",
        type: "warning",
        hideCancel: true,
      });
    } finally {
      setLoading(false);
    }
  }, [
    branchId,
    clearPolygons,
    geocodeAddress,
    initMap,
    renderExistingZone,
    renderOtherZones,
    attachRightClickDistance,
  ]);

  useEffect(() => {
    const t = setTimeout(() => fetchBranchAndZone(), 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId]);

  useEffect(() => {
    const changed = () => {
      const fullscreen = document.fullscreenElement === mapContainerRef.current;
      setIsMapFullscreen(fullscreen);
      setTimeout(() => {
        if (mapRef.current && window.google) window.google.maps.event.trigger(mapRef.current, "resize");
      }, 0);
    };
    document.addEventListener("fullscreenchange", changed);
    return () => document.removeEventListener("fullscreenchange", changed);
  }, []);

  useEffect(() => {
    return () => {
      clearPolygons();
      clearOtherPolygons();
      clearEditPathListeners();
      overlapMarkersRef.current.forEach((marker) => marker.setMap(null));
      overlapMarkersRef.current = [];

      if (branchMarkerRef.current) branchMarkerRef.current.setMap(null);

      cleanupRightClick();
    };
  }, [clearEditPathListeners, clearOtherPolygons, clearPolygons, cleanupRightClick]);

  const startDraw = useCallback(async () => {
    try {
      const google = await loadGoogleMaps(["drawing", "geometry"]);
      if (!drawingRef.current) {
        showConfirm("Info", { description: "Map is not ready yet. Please wait.", type: "info", hideCancel: true });
        return;
      }

      clearPolygons();
      setZoneId(null);

      // allow right-click measuring during drawing also (optional)
      isEditableRef.current = true;
      setIsEditable(true);
      setDistanceKm(null);

      await attachRightClickDistance();

      drawingRef.current.setDrawingMode(google.maps.drawing.OverlayType.POLYGON);
    } catch (e) {
      showConfirm("Oops!", { description: e.message, type: "warning", hideCancel: true });
    }
  }, [attachRightClickDistance, clearPolygons]);

  const enableEdit = useCallback(async () => {
    const poly = existingPolygonRef.current || drawnPolygonRef.current;
    if (!poly) {
      showConfirm("Info", { description: "No zone polygon to edit.", type: "info", hideCancel: true });
      return;
    }

    poly.setEditable(true);
    poly.setOptions({ zIndex: 999 });

    isEditableRef.current = true;
    setIsEditable(true);
    setDistanceKm(null);
    editStartPathRef.current = poly.getPath().getArray().map((p) => ({ lat: p.lat(), lng: p.lng() }));
    acceptedOverlapScoreRef.current = getOverlapScore(editStartPathRef.current, otherZonesDataRef.current);
    watchEditablePath(poly);

    // important: reattach so polygon rightclick is bound
    await attachRightClickDistance();
  }, [attachRightClickDistance, watchEditablePath]);

  const stopEdit = useCallback(async () => {
    const poly = existingPolygonRef.current || drawnPolygonRef.current;
    if (!poly) return;

    poly.setEditable(false);

    isEditableRef.current = false;
    setIsEditable(false);
    setDistanceKm(null);
    if (document.fullscreenElement === mapContainerRef.current) await document.exitFullscreen();
    clearEditPathListeners();
    setMergeSuggestion(null);
    setOverlapZone(null);
    setOverlapDetails([]);
    overlapMarkersRef.current.forEach((marker) => marker.setMap(null));
    overlapMarkersRef.current = [];
    setEditNotice("");
    setSelectedVertexIndex(null);

    await attachRightClickDistance();
  }, [attachRightClickDistance, clearEditPathListeners]);

  const showOverlapOnMap = useCallback(() => {
    const points = overlapDetails.flatMap((detail) => detail.points);
    if (!points.length || !mapRef.current || !window.google) return;
    const bounds = new window.google.maps.LatLngBounds();
    points.forEach((point) => bounds.extend(point));
    mapRef.current.fitBounds(bounds, 90);
    window.google.maps.event.addListenerOnce(mapRef.current, "idle", () => {
      if (mapRef.current.getZoom() > 18) mapRef.current.setZoom(18);
    });
  }, [overlapDetails]);

  const handleMergeBorder = useCallback(() => {
    const poly = existingPolygonRef.current || drawnPolygonRef.current;
    if (!poly || !mergeSuggestion) return;
    const current = poly.getPath().getArray().map((p) => ({ lat: p.lat(), lng: p.lng() }));
    preMergePathRef.current = current;
    const candidates = getMergeCandidates(current, mergeSuggestion);
    const ranked = candidates.map((candidate) => ({ candidate, score: getOverlapScore(candidate, otherZonesDataRef.current) }))
      .sort((a, b) => a.score - b.score);
    const best = ranked[0];
    if (!best || best.score > acceptedOverlapScoreRef.current) {
      setEditNotice("Merge would increase overlap, so the current border was kept.");
      return;
    }
    poly.setPath(best.candidate);
    acceptedOverlapScoreRef.current = best.score;
    setOverlapZone(findOverlap(best.candidate, otherZonesDataRef.current));
    setEditNotice(best.score ? "Border merged and overlap reduced. Continue correcting the highlighted part." : "Border merged successfully. Save Zone is ready.");
    watchEditablePath(poly);
  }, [mergeSuggestion, watchEditablePath]);

  const undoMergeBorder = useCallback(() => {
    const poly = existingPolygonRef.current || drawnPolygonRef.current;
    if (!poly || !preMergePathRef.current) return;
    poly.setPath(preMergePathRef.current);
    acceptedOverlapScoreRef.current = getOverlapScore(preMergePathRef.current, otherZonesDataRef.current);
    preMergePathRef.current = null;
    watchEditablePath(poly);
  }, [watchEditablePath]);

  const deleteSelectedVertex = useCallback(() => {
    const poly = existingPolygonRef.current || drawnPolygonRef.current;
    if (!poly || !Number.isInteger(selectedVertexIndex)) return;
    const path = poly.getPath();
    if (path.getLength() <= 3) {
      setEditNotice("A delivery zone must keep at least 3 points.");
      return;
    }
    path.removeAt(selectedVertexIndex);
    setSelectedVertexIndex(null);
  }, [selectedVertexIndex]);

  const handleClearDraft = useCallback(async () => {
    const poly = existingPolygonRef.current || drawnPolygonRef.current;
    if (poly && editStartPathRef.current) {
      poly.setPath(editStartPathRef.current);
      acceptedOverlapScoreRef.current = getOverlapScore(editStartPathRef.current, otherZonesDataRef.current);
      watchEditablePath(poly);
    }
    setMergeSuggestion(null);
    setOverlapZone(null);
    setEditNotice("Changes cleared. The original saved border was restored.");
    preMergePathRef.current = null;

    await attachRightClickDistance();
  }, [attachRightClickDistance, watchEditablePath]);

  const toggleMapFullscreen = useCallback(async () => {
    try {
      if (document.fullscreenElement === mapContainerRef.current) {
        await document.exitFullscreen();
      } else if (mapContainerRef.current?.requestFullscreen) {
        await mapContainerRef.current.requestFullscreen();
      } else {
        throw new Error("Fullscreen is not supported by this browser.");
      }
    } catch (error) {
      showConfirm("Fullscreen", { description: error.message, type: "warning", hideCancel: true });
    }
  }, []);

  const handleSave = useCallback(async () => {
    const poly = drawnPolygonRef.current || existingPolygonRef.current;
    if (!poly) {
      showConfirm("Info", { description: "Please draw a zone polygon first.", type: "info", hideCancel: true });
      return;
    }

    try {
      setSaving(true);
      const geojson = polygonToGeoJSON(poly);

      if (!zoneId) {
        const resp = await branchZonesApi.createZone({
          branchId: Number(branchId),
          zoneType,
          priority,
          zoneName: zoneName || null,
          geojson,
        });

        if (!resp?.status) throw new Error(resp?.message || "Failed to create zone");

        if (document.fullscreenElement === mapContainerRef.current) await document.exitFullscreen();

        showConfirm("Success", { description: resp.message || "Zone created successfully", type: "Success", hideCancel: true })
          .then(() => fetchBranchAndZone());
      } else {
        const resp = await branchZonesApi.updateZone({
          zoneId,
          zoneType,
          priority,
          zoneName: zoneName || null,
          geojson,
          note: "Zone updated from admin UI",
        });

        if (!resp?.status) throw new Error(resp?.message || "Failed to update zone");

        if (document.fullscreenElement === mapContainerRef.current) await document.exitFullscreen();

        showConfirm("Success", { description: resp.message || "Zone updated successfully", type: "Success", hideCancel: true })
          .then(() => fetchBranchAndZone());
      }
    } catch (e) {
      console.error(e);
      showConfirm("Oops!", { description: e.message || "Save failed", type: "warning", hideCancel: true });
    } finally {
      setSaving(false);
    }
  }, [branchId, fetchBranchAndZone, zoneId, zoneName]);

  const handleDelete = useCallback(async () => {
    if (!zoneId) {
      showConfirm("Info", { description: "No saved zone to delete.", type: "info", hideCancel: true });
      return;
    }

    showConfirm("Are you sure?", { description: "This will delete the delivery zone.", type: "warning" })
      .then(async (answer) => {
        if (!answer) return;

        try {
          const resp = await branchZonesApi.deleteZone({ zoneId, reason: "Deleted from admin UI" });
          if (!resp?.status) throw new Error(resp?.message || "Delete failed");

          showConfirm("Success", { description: resp.message || "Zone deleted", type: "Success", hideCancel: true })
            .then(() => fetchBranchAndZone());
        } catch (e) {
          console.error(e);
          showConfirm("Oops!", { description: e.message || "Delete failed", type: "warning", hideCancel: true });
        }
      });
  }, [fetchBranchAndZone, zoneId]);

  return (
    <Container className="mt-4 mb-4">
      <DynamicBreadcrumb pathArray={pathArray} />

      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5>Delivery Zone - {branch?.branchName || branch?.name || `Branch #${branchId}`}</h5>
        <Button variant="secondary" onClick={() => navigate(-1)}>Back</Button>
      </div>

      <Row>
        <Col md={4} className="mb-3">
          <div className="p-3 border rounded bg-white">
            <div className="mb-2">
              <Form.Label className="fw-bold">Zone Type</Form.Label>
              <Form.Control value="PRIMARY" disabled />
              <Form.Text className="text-muted">For now, we maintain only one PRIMARY zone per branch.</Form.Text>
            </div>

            <div className="mb-2">
              <Form.Label className="fw-bold">Zone Name (optional)</Form.Label>
              <Form.Control
                value={zoneName}
                placeholder="e.g. Karaikudi Core"
                onChange={(e) => setZoneName(e.target.value)}
                disabled={saving}
              />
            </div>

            <div className="d-grid gap-2 mt-3">
              {!zoneId && (
                <Button variant="primary" onClick={startDraw} disabled={saving}>Draw Zone</Button>
              )}
              <Button variant="outline-primary" onClick={enableEdit} disabled={saving || isEditable}>Edit Zone</Button>

              {isEditable && (
                <Button variant="outline-secondary" onClick={stopEdit} disabled={saving}>Stop Editing</Button>
              )}

              {isEditable && (
                <div className="small border rounded p-2 bg-light">
                  Click a white dot and use Delete Point, or right-click it to delete immediately.
                  {Number.isInteger(selectedVertexIndex) && <b>{` Point #${selectedVertexIndex + 1} selected.`}</b>}
                </div>
              )}

              {isEditable && (
                <Button variant="outline-danger" onClick={deleteSelectedVertex} disabled={saving || !Number.isInteger(selectedVertexIndex)}>
                  Delete Selected Point
                </Button>
              )}

              {isEditable && mergeSuggestion && (
                <div className="border border-warning rounded p-2 bg-light">
                  <div className="small mb-2">
                    Border is within 40 ft of <b>{mergeSuggestion.branchName}</b>
                    {` (${mergeSuggestion.closestMeters.toFixed(1)} m away)`}. Merge the nearby points?
                  </div>
                  <Button size="sm" variant="warning" className="w-100" onClick={handleMergeBorder}>
                    Merge Border
                  </Button>
                </div>
              )}

              {isEditable && overlapZone && (
                <div className="alert alert-danger py-2 small mb-0">
                  <div>
                    Overlap detected with <b>{overlapDetails.map((detail) => detail.zone.branchName || `Branch #${detail.zone.branchId}`).join(", ")}</b>.
                    Red circles mark the exact conflicting places.
                  </div>
                  <Button size="sm" variant="danger" className="w-100 mt-2" onClick={showOverlapOnMap}>Show Overlap on Map</Button>
                </div>
              )}

              {isEditable && preMergePathRef.current && (
                <Button variant="outline-secondary" onClick={undoMergeBorder} disabled={saving}>Undo Border Merge</Button>
              )}

              {isEditable && (
                <Button variant="outline-danger" onClick={handleClearDraft} disabled={saving}>Clear Changes</Button>
              )}

              {isEditable && (
                <Button variant="success" onClick={handleSave} disabled={saving || Boolean(overlapZone)}>
                  {saving ? "Saving..." : "Save Zone"}
                </Button>
              )}

              <Button variant="danger" onClick={handleDelete} disabled={saving || !zoneId}>
                Delete Saved Zone
              </Button>
            </div>
          </div>
        </Col>

        <Col md={8}>
          <div ref={mapContainerRef} className="border rounded position-relative bg-white" style={{ height: isMapFullscreen ? "100vh" : 560, overflow: "hidden" }}>
            <div ref={mapDivRef} style={{ width: "100%", height: "100%" }} />

            {isEditable && (
              <button
                type="button"
                title={isMapFullscreen ? "Exit fullscreen" : "Open fullscreen editor"}
                aria-label={isMapFullscreen ? "Exit fullscreen" : "Open fullscreen editor"}
                onClick={toggleMapFullscreen}
                className="position-absolute bg-white border-0 shadow-sm rounded d-flex align-items-center justify-content-center"
                style={{ right: 10, ...(isMapFullscreen ? { bottom: 52 } : { top: 10 }), zIndex: 5, width: 40, height: 40, fontSize: 24, lineHeight: 1 }}
              >
                {isMapFullscreen ? "×" : "⛶"}
              </button>
            )}

            {isMapFullscreen && (
              <div
                className="position-absolute d-flex gap-2 align-items-center p-2 rounded bg-white shadow"
                style={{ left: 12, top: 12, zIndex: 4 }}
              >
                <Button size="sm" variant="success" onClick={handleSave} disabled={saving || Boolean(overlapZone)}>
                  {saving ? "Saving..." : "Save Zone"}
                </Button>
                <Button size="sm" variant="outline-danger" onClick={handleClearDraft} disabled={saving}>Clear Changes</Button>
                <Button size="sm" variant="secondary" onClick={stopEdit} disabled={saving}>Finish Editing</Button>
                <span className="small text-muted">Right-click a white dot to delete it</span>
              </div>
            )}

            {isMapFullscreen && editNotice && (
              <div className="position-absolute alert alert-warning py-2 mb-0" style={{ left: 12, top: 64, zIndex: 4 }}>
                {editNotice}
              </div>
            )}

            {isMapFullscreen && mergeSuggestion && (
              <div className="position-absolute border border-warning rounded bg-white shadow p-2" style={{ right: 12, top: 12, zIndex: 4, maxWidth: 340 }}>
                <div className="small mb-2">
                  Border is within 40 ft of <b>{mergeSuggestion.branchName}</b>. Merge using the safe non-overlapping border?
                </div>
                <Button size="sm" variant="warning" className="w-100" onClick={handleMergeBorder}>Merge Border</Button>
              </div>
            )}

            {isMapFullscreen && overlapDetails.length > 0 && (
              <Button
                size="sm"
                variant="danger"
                className="position-absolute"
                style={{ right: 12, top: mergeSuggestion ? 126 : 12, zIndex: 4 }}
                onClick={showOverlapOnMap}
              >
                Show {overlapDetails.reduce((count, detail) => count + detail.points.length, 0)} Overlap Point(s)
              </Button>
            )}

            {isEditable && distanceKm !== null && (
              <div
                className="position-absolute"
                style={{
                  left: 12,
                  bottom: 12,
                  zIndex: 3,
                  background: "rgba(0,0,0,0.70)",
                  color: "#fff",
                  padding: "8px 10px",
                  borderRadius: 8,
                  fontSize: 12,
                  lineHeight: 1.2,
                }}
              >
                Distance from Branch: <b>{distanceKm.toFixed(2)} km</b>
                <div style={{ opacity: 0.85, marginTop: 4 }}>Right-click again to update</div>
              </div>
            )}

            {loading && (
              <div
                className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                style={{ background: "rgba(255,255,255,0.65)", zIndex: 2 }}
              >
                <div className="d-flex align-items-center gap-2">
                  <Spinner size="sm" /> <span>Loading map...</span>
                </div>
              </div>
            )}
          </div>

          <div className="text-muted mt-2" style={{ fontSize: 12 }}>
            Tip: While editing, <b>right-click</b> anywhere (even on the polygon) to measure distance from branch.
          </div>
        </Col>
      </Row>
    </Container>
  );
}
